import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  BrowserRouter,
  Routes,
  Route,
  createRoutesFromElements,
} from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Docs from "./pages/Docs";
import { VerifyEmail } from "./pages/VerifyEmail";
import RecoveryPassword from "./pages/RecoveryPassword";
import SetPassword from "./pages/SetPassword";
import { AuthDebug } from "./components/AuthDebug";
import { AuditPage } from "./pages/AuditPage";
import { ProductsPage } from "./pages/ProductsPage";
import { ClientsPage } from "./pages/ClientsPage";
import { ProfilesPage } from "./pages/ProfilesPage";
import { UsersPage } from "./pages/UsersPage";
import { AccessValidationPage } from "./pages/AccessValidationPage";
import { AIProvidersPage } from "./pages/AIProvidersPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AgentsPage } from "./pages/AgentsPage";
import { WorkspacesPage } from "./pages/WorkspacesPage";
import { SandboxPage } from "./pages/SandboxPage";
import { ReportsPage } from "./pages/ReportsPage";
import { AllApiCallsPage } from "./pages/AllApiCallsPage";
import { ClientApiPage } from "./pages/ClientApiPage";
import { PlansPage } from "./pages/PlansPage";
import Forbidden from "./pages/Forbidden";
import { AuthRouteEffects } from "@/components/auth/AuthRouteEffects";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";

const appVersion = import.meta.env.VITE_APP_VERSION || "dev";

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthDebug />
          <span
            className="fixed bottom-1 right-2 text-[10px] text-muted-foreground/40 select-none pointer-events-none z-50"
            aria-hidden="true"
          >
            {appVersion}
          </span>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthRouteEffects />
            <Routes>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="/docs" element={<Docs />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/recovery-password" element={<RecoveryPassword />} />

              <Route element={<AppLayout />}>
                <Route path="/" element={<Index />} />

                {/* Existing feature pages */}
                <Route path="/audit" element={<ProtectedRoute requiredPermission="res_audit.read"><AuditPage /></ProtectedRoute>} />
                <Route path="/products" element={<ProtectedRoute requiredPermission="res_products.read"><ProductsPage /></ProtectedRoute>} />
                <Route path="/plans" element={<ProtectedRoute requiredPermission="res_plans.read"><PlansPage /></ProtectedRoute>} />
                <Route path="/clients" element={<ProtectedRoute requiredPermission="res_clients.read"><ClientsPage /></ProtectedRoute>} />

                {/* Newly added pages so the URL reflects navigation */}
                <Route path="/profiles" element={<ProtectedRoute requiredPermission="res_profiles.read"><ProfilesPage /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute requiredPermission="res_users.read"><UsersPage /></ProtectedRoute>} />
                <Route path="/access-validation" element={<ProtectedRoute requiredPermission="res_access_control.validate"><AccessValidationPage /></ProtectedRoute>} />
                <Route path="/ai-providers" element={<ProtectedRoute requiredPermission="res_providers.read"><AIProvidersPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute requiredPermission="res_models.read"><SettingsPage /></ProtectedRoute>} />
                <Route path="/agents" element={<ProtectedRoute requiredPermission="res_agents.read"><AgentsPage /></ProtectedRoute>} />
                <Route path="/workspaces" element={<ProtectedRoute requiredPermission="res_workspaces.read"><WorkspacesPage /></ProtectedRoute>} />
                <Route path="/sandbox" element={<ProtectedRoute requiredPermission="res_sandbox.read"><SandboxPage /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute requiredPermission="res_reports.read"><ReportsPage /></ProtectedRoute>} />
                <Route path="/all-api-calls" element={<ProtectedRoute requiredPermissions={["res_api_calls.read","res_api_calls_security.read"]} requireAll={false}><AllApiCallsPage /></ProtectedRoute>} />
                <Route path="/client-api" element={<ProtectedRoute requiredPermission="res_client_api.read"><ClientApiPage /></ProtectedRoute>} />

                <Route path="/forbidden" element={<Forbidden />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
